/* MediaLibraryPanel — centralized CMS media manager (Phase A).
 *
 * Layout:
 *   ┌─ Header: stats + upload button + search + status filter
 *   ├─ Left rail: album list (protected shows lock, click filters grid)
 *   └─ Right: media grid + detail drawer (click a thumbnail to edit)
 *
 * Everything auto-publishes on upload — no separate "publish" step.
 * Drop files anywhere on the grid to trigger upload into the currently
 * selected album (or "no album" if All is selected).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Image as ImageIcon, Upload, Search, Loader2, Trash2, Edit3, Plus,
  Lock, FolderPlus, X, Save, RefreshCw, CheckCircle2, Cloud,
  AlertTriangle, Filter, Grid, Copy, Tag, ArrowUpDown, GripVertical,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin/media`;
const SERVE = `${BACKEND_URL}/api/media/serve`;

const fmtBytes = (n) => {
  if (!n && n !== 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
};
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
};

const MediaLibraryPanel = () => {
  const [albums, setAlbums] = useState([]);
  const [media, setMedia] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedAlbum, setSelectedAlbum] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [editingMedia, setEditingMedia] = useState(null);
  const [showNewAlbum, setShowNewAlbum] = useState(false);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  // Reorder mode — manual pinned order for native Media Library photos
  // inside a specific album. Only enabled when a specific album is
  // selected (not 'all'). Publications are NOT reordered here.
  const [reorderMode, setReorderMode] = useState(false);
  const [orderedMedia, setOrderedMedia] = useState([]);
  const [orderDirty, setOrderDirty] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [reorderDragIdx, setReorderDragIdx] = useState(null);
  const [reorderOverIdx, setReorderOverIdx] = useState(null);
  const fileInputRef = useRef(null);

  const loadAlbums = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/albums`, { withCredentials: true });
      setAlbums(r.data.rows || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load albums.');
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/_stats`, { withCredentials: true });
      setStats(r.data);
    } catch (e) {
      // Non-fatal
    }
  }, []);

  const loadMedia = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100, status: statusFilter };
      if (selectedAlbum !== 'all') params.album_id = selectedAlbum;
      if (search.trim()) params.q = search.trim();
      const r = await axios.get(API, { withCredentials: true, params });
      setMedia(r.data.rows || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load media.');
    }
    setLoading(false);
  }, [selectedAlbum, statusFilter, search]);

  useEffect(() => { loadAlbums(); loadStats(); }, [loadAlbums, loadStats]);
  useEffect(() => { loadMedia(); }, [loadMedia]);

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    setUploading(true); setError('');
    setUploadProgress({ done: 0, total: files.length });
    const form = new FormData();
    Array.from(files).forEach((f) => form.append('files', f));
    if (selectedAlbum !== 'all') form.append('album_ids', selectedAlbum);
    try {
      const r = await axios.post(API, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (p) => {
          if (p.total) setUploadProgress({
            done: Math.round((p.loaded / p.total) * files.length),
            total: files.length });
        },
      });
      if (r.data.errors?.length) {
        setError(`Some files failed: ${r.data.errors.map(
          (e) => `${e.filename}: ${e.error}`).join('; ')}`);
      }
      await Promise.all([loadMedia(), loadStats(), loadAlbums()]);
    } catch (e) {
      setError(e.response?.data?.detail || 'Upload failed.');
    }
    setUploading(false);
    setUploadProgress(null);
  };

  const albumById = useMemo(() =>
    Object.fromEntries(albums.map((a) => [a.id, a])), [albums]);

  // ---------- Reorder mode ----------
  // Exit reorder mode automatically if the admin switches album or
  // status filter — the ordered array wouldn't make sense anymore.
  // Exit reorder mode automatically if the admin switches album or
  // status filter — the ordered array wouldn't make sense anymore.
  // NB: `reorderMode` is intentionally NOT in the deps — including it
  // would cause the effect to fire the instant we set it true and
  // immediately flip it back to false (self-cancellation).
  useEffect(() => {
    if (reorderMode) {
      setReorderMode(false);
      setOrderedMedia([]);
      setOrderDirty(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAlbum, statusFilter]);

  const enterReorderMode = () => {
    if (selectedAlbum === 'all') return;
    const album = albumById[selectedAlbum];
    const pinned = (album && album.photo_order) || [];
    // Sort current media by pinned order first, then by uploaded_at desc.
    const pinnedIdx = new Map(pinned.map((id, i) => [id, i]));
    const sorted = [...media].sort((a, b) => {
      const ai = pinnedIdx.has(a.id) ? pinnedIdx.get(a.id) : Infinity;
      const bi = pinnedIdx.has(b.id) ? pinnedIdx.get(b.id) : Infinity;
      if (ai !== bi) return ai - bi;
      // Fall back to newest-first for anything not pinned.
      return (b.uploaded_at || '').localeCompare(a.uploaded_at || '');
    });
    setOrderedMedia(sorted);
    setOrderDirty(false);
    setReorderMode(true);
  };

  const exitReorderMode = () => {
    setReorderMode(false);
    setOrderedMedia([]);
    setOrderDirty(false);
    setReorderDragIdx(null);
    setReorderOverIdx(null);
  };

  const handleReorderDragStart = (idx) => (e) => {
    setReorderDragIdx(idx);
    // Use a reorder-specific data type so the "drop to section" rail
    // ignores this drag (that rail listens for x-nhf-media-id).
    e.dataTransfer.setData('application/x-nhf-reorder-idx', String(idx));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleReorderDragOver = (idx) => (e) => {
    if (reorderDragIdx === null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (idx !== reorderOverIdx) setReorderOverIdx(idx);
  };

  const handleReorderDrop = (idx) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (reorderDragIdx === null || reorderDragIdx === idx) {
      setReorderDragIdx(null); setReorderOverIdx(null); return;
    }
    setOrderedMedia((prev) => {
      const next = prev.slice();
      const [moved] = next.splice(reorderDragIdx, 1);
      next.splice(idx, 0, moved);
      return next;
    });
    setOrderDirty(true);
    setReorderDragIdx(null);
    setReorderOverIdx(null);
  };

  const saveOrder = async () => {
    if (selectedAlbum === 'all') return;
    setSavingOrder(true); setError('');
    try {
      await axios.patch(
        `${API}/albums/${selectedAlbum}/photo-order`,
        { order: orderedMedia.map((m) => m.id) },
        { withCredentials: true });
      await loadAlbums();  // refresh photo_order on the album doc
      exitReorderMode();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save photo order.');
    }
    setSavingOrder(false);
  };

  return (
    <div className="space-y-4" data-testid="media-library-panel">
      {/* Header + stats */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-heading font-bold text-base text-[#0A0A0A]
                          inline-flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-[#BA1826]" />
              Media Library
            </h2>
            <p className="text-[11px] text-[#4B5563] mt-1">
              Upload once, organize into albums, reuse everywhere.
              Files auto-optimize + auto-publish on upload.
            </p>
          </div>
          <div className="flex items-center gap-4 text-[11px] text-[#4B5563]">
            {stats && (
              <>
                <div><span className="font-bold text-[#0A0A0A]">{stats.total_active}</span> active</div>
                {stats.pending_review > 0 && (
                  <div><span className="font-bold text-amber-700">{stats.pending_review}</span> pending</div>
                )}
                <div><span className="font-bold text-[#0A0A0A]">{stats.albums}</span> albums</div>
                <div>{fmtBytes(stats.storage_bytes)} used</div>
              </>
            )}
            <button onClick={() => { loadMedia(); loadStats(); loadAlbums(); }}
              className="p-1.5 hover:text-[#BA1826]" data-testid="media-refresh"
              title="Refresh">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Controls */}
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search title, filename, tags…"
              className="w-full pl-8 pr-3 py-1.5 text-[11px] border border-[#E5E7EB] rounded"
              data-testid="media-search-input" />
          </div>
          <select value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-[11px] px-2 py-1.5 border border-[#E5E7EB] rounded bg-white"
            data-testid="media-status-filter">
            <option value="active">Active</option>
            <option value="pending">Pending review</option>
            <option value="archived">Archived</option>
          </select>
          <button onClick={() => fileInputRef.current?.click()}
            disabled={uploading || reorderMode}
            className="text-xs px-3 py-1.5 bg-[#BA1826] text-white rounded
                      inline-flex items-center gap-1.5 disabled:opacity-60"
            data-testid="media-upload-btn">
            {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
            {uploading ? `Uploading ${uploadProgress?.done || 0}/${uploadProgress?.total || 0}…` : 'Upload media'}
          </button>
          <input ref={fileInputRef} type="file" multiple accept="image/*"
            className="hidden"
            onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
            data-testid="media-upload-input" />
          {/* Reorder Photos toggle — only usable when a specific album
              is selected (not 'all'). */}
          {!reorderMode ? (
            <button onClick={enterReorderMode}
              disabled={selectedAlbum === 'all' || media.length < 2}
              className="text-xs px-3 py-1.5 border border-[#E5E7EB]
                        rounded inline-flex items-center gap-1.5
                        text-[#4B5563] hover:bg-[#F8F9FA]
                        disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="album-reorder-toggle"
              title={selectedAlbum === 'all'
                ? 'Select a specific album first'
                : media.length < 2 ? 'Need at least 2 photos to reorder'
                : 'Drag photos to reorder them within this album'}>
              <ArrowUpDown className="w-3 h-3" /> Reorder Photos
            </button>
          ) : (
            <>
              <button onClick={saveOrder}
                disabled={!orderDirty || savingOrder}
                className="text-xs px-3 py-1.5 rounded bg-[#0A0A0A]
                          text-white inline-flex items-center gap-1.5
                          hover:bg-black disabled:opacity-40"
                data-testid="album-reorder-save">
                {savingOrder ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                Save order
              </button>
              <button onClick={exitReorderMode}
                disabled={savingOrder}
                className="text-xs px-3 py-1.5 rounded border border-[#E5E7EB]
                          text-[#4B5563] hover:bg-[#F8F9FA] inline-flex
                          items-center gap-1.5 disabled:opacity-40"
                data-testid="album-reorder-discard">
                <X className="w-3 h-3" /> Discard
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700
                       text-[11px] p-2 rounded inline-flex items-start gap-2"
          data-testid="media-error">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />
          <div className="flex-1">{error}</div>
          <button onClick={() => setError('')}><X className="w-3 h-3" /></button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr_240px] gap-4">
        {/* Album sidebar */}
        <AlbumSidebar albums={albums} selectedAlbum={selectedAlbum}
          onSelect={setSelectedAlbum}
          onCreateClick={() => setShowNewAlbum(true)}
          onAlbumChanged={() => { loadAlbums(); loadStats(); }} />

        {/* Media grid */}
        <div
          onDragOver={(e) => {
            if (reorderMode) return;
            e.preventDefault(); setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            if (reorderMode) return;
            e.preventDefault(); setDragOver(false);
            handleFiles(e.dataTransfer.files);
          }}
          className={`min-h-[400px] rounded p-3 transition ${
            reorderMode
              ? 'bg-[#FFF8EE] border-2 border-dashed border-amber-500'
              : dragOver
                ? 'bg-[#FFF8F8] border-2 border-dashed border-[#BA1826]'
                : 'bg-[#FAFBFC] border border-[#E5E7EB]'
          }`}
          data-testid="media-grid-dropzone">
          {reorderMode && (
            <div className="text-[11px] text-amber-800 mb-2 flex items-center gap-1.5"
              data-testid="album-reorder-banner">
              <GripVertical className="w-3 h-3" />
              <strong>Reorder mode:</strong> Drag photos to change their order in
              this album. Only native Media Library photos are reordered;
              published references keep their default order.
            </div>
          )}
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-[#BA1826]" />
            </div>
          ) : media.length === 0 ? (
            <div className="text-center py-16 text-xs text-[#9CA3AF]">
              <Upload className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p>Drag & drop images here, or click <strong>Upload media</strong> above.</p>
              <p className="mt-1">Files are auto-optimized into 4 responsive sizes + WebP.</p>
            </div>
          ) : reorderMode ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2"
              data-testid="media-grid-reorder">
              {orderedMedia.map((m, idx) => (
                <div key={m.id}
                  draggable
                  onDragStart={handleReorderDragStart(idx)}
                  onDragOver={handleReorderDragOver(idx)}
                  onDrop={handleReorderDrop(idx)}
                  onDragEnd={() => {
                    setReorderDragIdx(null); setReorderOverIdx(null);
                  }}
                  className={`relative aspect-square rounded overflow-hidden
                    border-2 bg-white cursor-grab active:cursor-grabbing
                    ${reorderOverIdx === idx
                      ? 'border-amber-500 ring-2 ring-amber-500/30'
                      : reorderDragIdx === idx
                        ? 'border-amber-500 opacity-50'
                        : 'border-[#E5E7EB] hover:border-amber-500'}`}
                  data-testid={`media-card-reorder-${m.id}`}
                  aria-label={`Reorder position ${idx + 1}`}>
                  <img src={`${SERVE}/${m.id}/thumb`}
                    alt={m.alt_text || m.title || ''}
                    className="w-full h-full object-cover pointer-events-none"
                    loading="lazy" />
                  <span className="absolute top-1 left-1 text-[10px]
                                  bg-amber-500 text-white px-1.5 py-0.5
                                  rounded font-bold">
                    {idx + 1}
                  </span>
                  <span className="absolute top-1 right-1 text-white/90
                                  bg-black/40 rounded p-0.5">
                    <GripVertical className="w-3 h-3" />
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2"
              data-testid="media-grid">
              {media.map((m) => (
                <MediaCard key={m.id} m={m} albumById={albumById}
                  onClick={() => setEditingMedia(m)} />
              ))}
            </div>
          )}
        </div>

        {/* Sections drop rail — drag a photo here to instantly assign
            it to a site section. Single-mode sections REPLACE, slideshow
            sections APPEND. */}
        <SectionsDropRail onError={setError}
          onAssigned={() => { loadMedia(); loadStats(); }} />
      </div>

      {editingMedia && (
        <MediaEditDrawer m={editingMedia} albums={albums}
          onClose={() => setEditingMedia(null)}
          onSaved={() => { loadMedia(); loadStats(); loadAlbums(); }} />
      )}

      {showNewAlbum && (
        <NewAlbumModal onClose={() => setShowNewAlbum(false)}
          onCreated={() => { setShowNewAlbum(false); loadAlbums(); loadStats(); }} />
      )}
    </div>
  );
};

const SECTIONS_API = `${BACKEND_URL}/api/admin/site-sections`;

// Only sections that are actually mounted on the public site can be
// dropped onto — anything else would silently succeed but have no
// visible effect. Keep this in sync with SectionsConfigPanel's
// MOUNTED_SECTIONS Set.
const MOUNTED_SECTIONS = [
  'home-mission', 'home-values', 'home-location',
  'about-hero', 'about-team',
  'country-haiti-hero', 'country-dominican-republic-hero',
  'country-colombia-yumbo-hero', 'country-elsalvador-hero',
  'country-ecuador-hero', 'country-jamaica-hero',
];

const SectionsDropRail = ({ onError, onAssigned }) => {
  const [sections, setSections] = useState([]);
  const [dropTargetKey, setDropTargetKey] = useState(null);
  const [flashKey, setFlashKey] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(SECTIONS_API, { withCredentials: true });
      setSections((r.data.rows || [])
        .filter((s) => MOUNTED_SECTIONS.includes(s.id)));
    } catch (e) {
      /* silent — the rail just won't populate */
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDrop = async (e, key) => {
    e.preventDefault();
    setDropTargetKey(null);
    const mediaId = e.dataTransfer.getData('application/x-nhf-media-id');
    if (!mediaId) return;
    onError('');
    try {
      await axios.post(`${SECTIONS_API}/${key}/assign-media/${mediaId}`, {},
        { withCredentials: true });
      setFlashKey(key);
      setTimeout(() => setFlashKey(null), 1500);
      load();  // refresh cover thumbs
      onAssigned();
    } catch (err) {
      onError(err.response?.data?.detail || 'Failed to assign to section.');
    }
  };

  return (
    <div className="bg-white border border-[#E5E7EB] rounded p-2 max-h-[600px]
                   overflow-y-auto" data-testid="sections-drop-rail">
      <div className="flex items-center gap-1.5 px-1 pb-1.5 border-b
                     border-[#F1F5F9]">
        <Grid className="w-3 h-3 text-[#BA1826]" />
        <p className="text-[10px] uppercase tracking-wider font-bold
                     text-[#4B5563]">
          Drop onto a section
        </p>
      </div>
      <p className="text-[9px] text-[#9CA3AF] px-1 mt-1 mb-1.5 leading-snug">
        Drag any photo above onto a section to instantly assign it.
        Single sections replace; slideshows append.
      </p>
      <ul className="space-y-1">
        {sections.map((s) => (
          <li key={s.id}>
            <div
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'copy';
                setDropTargetKey(s.id);
              }}
              onDragLeave={() => setDropTargetKey(
                (cur) => cur === s.id ? null : cur)}
              onDrop={(e) => handleDrop(e, s.id)}
              className={`flex items-center gap-2 p-1.5 rounded border
                transition ${
                  flashKey === s.id
                    ? 'bg-emerald-50 border-emerald-400'
                    : dropTargetKey === s.id
                      ? 'bg-sky-50 border-sky-400 border-dashed'
                      : 'bg-white border-[#E5E7EB] hover:border-[#BA1826]'
                }`}
              data-testid={`drop-target-${s.id}`}>
              {s.cover_media_id ? (
                <img src={`${SERVE}/${s.cover_media_id}/thumb`}
                  alt=""
                  className="w-9 h-9 object-cover rounded border
                            border-[#E5E7EB] shrink-0" />
              ) : (
                <div className="w-9 h-9 rounded border border-dashed
                              border-[#E5E7EB] bg-[#FAFBFC] shrink-0
                              flex items-center justify-center">
                  <ImageIcon className="w-3.5 h-3.5 text-[#9CA3AF]" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-semibold truncate leading-tight">
                  {s.label.replace(/^[^—]+—\s*/, '')}
                </p>
                <p className="text-[9px] text-[#9CA3AF] uppercase tracking-wider">
                  {s.page.replace('-', ' ')}
                  {s.display_mode === 'slideshow'
                    && ` · ${(s.media_ids || []).length} img`}
                </p>
              </div>
              {flashKey === s.id && (
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

const MediaCard = ({ m, albumById, onClick }) => {
  const [imgError, setImgError] = useState(false);
  return (
    <button onClick={onClick}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('application/x-nhf-media-id', m.id);
        e.dataTransfer.effectAllowed = 'copy';
      }}
      className="group relative aspect-square bg-white border border-[#E5E7EB]
                rounded overflow-hidden hover:border-[#BA1826] transition
                text-left cursor-grab active:cursor-grabbing"
      data-testid={`media-card-${m.id}`}>
      {imgError ? (
        <div className="w-full h-full flex items-center justify-center bg-[#FAFBFC]">
          <ImageIcon className="w-8 h-8 text-[#9CA3AF]" />
        </div>
      ) : (
        <img src={`${SERVE}/${m.id}/thumb`} alt={m.alt_text || m.title}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={() => setImgError(true)} />
      )}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t
                     from-black/70 to-transparent p-1.5 opacity-0
                     group-hover:opacity-100 transition">
        <p className="text-white text-[10px] font-bold truncate">
          {m.title || m.filename}
        </p>
        <p className="text-white/70 text-[9px]">
          {m.width}×{m.height} · {fmtBytes(m.size_bytes)}
        </p>
      </div>
      {m.status === 'pending' && (
        <span className="absolute top-1 left-1 text-[8px] bg-amber-500 text-white
                        px-1.5 py-0.5 rounded font-bold uppercase tracking-wider">
          Pending
        </span>
      )}
      {m.drive_file_id && (
        <span className="absolute top-1 right-1 bg-emerald-500 text-white
                        p-0.5 rounded" title="Synced to Drive">
          <CheckCircle2 className="w-2.5 h-2.5" />
        </span>
      )}
      {!m.drive_file_id && m.drive_sync_error && (
        <span className="absolute top-1 right-1 bg-rose-500 text-white
                        p-0.5 rounded" title={m.drive_sync_error}>
          <AlertTriangle className="w-2.5 h-2.5" />
        </span>
      )}
      {(m.album_ids || []).length > 0 && (
        <span className="absolute bottom-1 right-1 text-[8px] bg-black/60
                        text-white px-1 py-0.5 rounded"
          title={m.album_ids.map((aid) => albumById[aid]?.name || aid).join(', ')}>
          {m.album_ids.length} album{m.album_ids.length > 1 ? 's' : ''}
        </span>
      )}
    </button>
  );
};

const AlbumSidebar = ({ albums, selectedAlbum, onSelect, onCreateClick,
                            onAlbumChanged }) => (
  <div className="bg-white border border-[#E5E7EB] rounded p-2"
    data-testid="album-sidebar">
    <div className="flex items-center justify-between px-1 pb-1.5 border-b border-[#F1F5F9]">
      <p className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">
        Albums
      </p>
      <button onClick={onCreateClick}
        className="text-[10px] text-[#BA1826] hover:underline inline-flex items-center gap-0.5"
        data-testid="album-new-btn">
        <FolderPlus className="w-3 h-3" /> New
      </button>
    </div>
    <ul className="mt-1 space-y-0.5 max-h-[520px] overflow-y-auto">
      <li>
        <button onClick={() => onSelect('all')}
          className={`w-full text-left text-[11px] px-2 py-1.5 rounded
            ${selectedAlbum === 'all'
              ? 'bg-[#BA1826] text-white' : 'hover:bg-[#FAFBFC] text-[#0A0A0A]'}`}
          data-testid="album-all">
          All media
        </button>
      </li>
      {albums.map((a) => (
        <li key={a.id}>
          <button onClick={() => onSelect(a.id)}
            className={`w-full text-left text-[11px] px-2 py-1.5 rounded
              inline-flex items-center gap-1.5
              ${selectedAlbum === a.id
                ? 'bg-[#BA1826] text-white'
                : 'hover:bg-[#FAFBFC] text-[#0A0A0A]'}`}
            data-testid={`album-${a.slug}`}>
            {a.is_protected && <Lock className="w-2.5 h-2.5 opacity-60" />}
            <span className="flex-1 truncate">{a.name}</span>
            <span className={`text-[9px] ${
              selectedAlbum === a.id ? 'text-white/80' : 'text-[#9CA3AF]'
            }`}>{a.media_count}</span>
          </button>
        </li>
      ))}
    </ul>
  </div>
);

const NewAlbumModal = ({ onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    if (!name.trim()) return;
    setSaving(true); setErr('');
    try {
      await axios.post(`${API}/albums`, { name: name.trim(), description },
        { withCredentials: true });
      onCreated();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to create album.');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center
                   justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg p-4 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        data-testid="new-album-modal">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold">Create album</h3>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <input value={name} onChange={(e) => setName(e.target.value)}
          placeholder="Album name (e.g. Winter 2026 Coat Drive)"
          className="w-full text-xs px-2 py-1.5 border border-[#E5E7EB] rounded mb-2"
          data-testid="new-album-name" />
        <textarea value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Description (optional)"
          rows={2}
          className="w-full text-xs px-2 py-1.5 border border-[#E5E7EB] rounded"
          data-testid="new-album-description" />
        {err && <p className="text-[11px] text-rose-700 mt-2">{err}</p>}
        <div className="flex justify-end gap-2 mt-3">
          <button onClick={onClose}
            className="text-xs px-3 py-1.5 border border-[#E5E7EB] rounded">
            Cancel
          </button>
          <button onClick={submit} disabled={saving || !name.trim()}
            className="text-xs px-3 py-1.5 bg-[#BA1826] text-white rounded
                      inline-flex items-center gap-1 disabled:opacity-60"
            data-testid="new-album-save">
            {saving && <Loader2 className="w-3 h-3 animate-spin" />}
            Create
          </button>
        </div>
      </div>
    </div>
  );
};

const MediaEditDrawer = ({ m, albums, onClose, onSaved }) => {
  const [form, setForm] = useState({
    title: m.title || '',
    description: m.description || '',
    alt_text: m.alt_text || '',
    credit: m.credit || '',
    taken_at: m.taken_at || '',
    tags: (m.tags || []).join(', '),
    album_ids: m.album_ids || [],
    status: m.status || 'active',
  });
  const [saving, setSaving] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [err, setErr] = useState('');
  const replaceInputRef = useRef(null);

  const save = async () => {
    setSaving(true); setErr('');
    try {
      await axios.patch(`${API}/${m.id}`, {
        title: form.title,
        description: form.description,
        alt_text: form.alt_text,
        credit: form.credit,
        taken_at: form.taken_at || null,
        tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
        album_ids: form.album_ids,
        status: form.status,
      }, { withCredentials: true });
      onSaved();
      onClose();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Save failed.');
    }
    setSaving(false);
  };

  const doDelete = async () => {
    if (!window.confirm(`Delete "${m.title || m.filename}"? This removes the file from NHF and Google Drive.`)) return;
    try {
      await axios.delete(`${API}/${m.id}`, { withCredentials: true });
      onSaved(); onClose();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Delete failed.');
    }
  };

  const doReplace = async (file) => {
    if (!file) return;
    setReplacing(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/${m.id}/replace`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' } });
      onSaved(); onClose();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Replace failed.');
    }
    setReplacing(false);
  };

  const toggleAlbum = (aid) => {
    setForm((f) => ({
      ...f,
      album_ids: f.album_ids.includes(aid)
        ? f.album_ids.filter((x) => x !== aid)
        : [...f.album_ids, aid],
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end"
      onClick={onClose}>
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="media-edit-drawer">
        <div className="sticky top-0 bg-white border-b border-[#E5E7EB]
                       px-4 py-3 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold">{m.title || m.filename}</h3>
            <p className="text-[10px] text-[#4B5563]">
              {m.width}×{m.height} · {fmtBytes(m.size_bytes)} · uploaded {fmtDate(m.uploaded_at)}
            </p>
          </div>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>

        <div className="p-4 space-y-3">
          {/* Preview */}
          <div className="bg-[#FAFBFC] rounded overflow-hidden
                         border border-[#E5E7EB] max-h-80 flex items-center
                         justify-center">
            <img src={`${SERVE}/${m.id}/desktop`}
              alt={m.alt_text || m.title}
              className="max-h-80 object-contain"
              data-testid="media-detail-preview" />
          </div>

          {/* Variant summary */}
          <div className="grid grid-cols-4 gap-1 text-[9px]">
            {Object.entries(m.variants || {}).map(([label, v]) => (
              <div key={label} className="border border-[#E5E7EB] rounded p-1.5">
                <p className="uppercase tracking-wider font-bold text-[#4B5563]">
                  {label}
                </p>
                <p className="text-[#0A0A0A]">{v.width}×{v.height}</p>
                <p className="text-[#9CA3AF]">{fmtBytes(v.bytes)}</p>
              </div>
            ))}
          </div>

          {/* Metadata form */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <Field label="Title" value={form.title}
              onChange={(v) => setForm({ ...form, title: v })}
              testid="media-edit-title" />
            <Field label="Photographer / Credit" value={form.credit}
              onChange={(v) => setForm({ ...form, credit: v })}
              testid="media-edit-credit" />
            <Field label="Alt text (for accessibility)"
              value={form.alt_text}
              onChange={(v) => setForm({ ...form, alt_text: v })}
              testid="media-edit-alt" />
            <Field label="Date taken (YYYY-MM-DD)" value={form.taken_at}
              onChange={(v) => setForm({ ...form, taken_at: v })}
              testid="media-edit-taken" />
          </div>
          <Field label="Description" value={form.description}
            onChange={(v) => setForm({ ...form, description: v })}
            multiline testid="media-edit-description" />
          <Field label="Tags (comma-separated)" value={form.tags}
            onChange={(v) => setForm({ ...form, tags: v })}
            testid="media-edit-tags" />

          {/* Album picker */}
          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold
                         text-[#4B5563] mb-1">Albums</p>
            <div className="flex flex-wrap gap-1"
              data-testid="media-edit-albums">
              {albums.map((a) => {
                const on = form.album_ids.includes(a.id);
                return (
                  <button key={a.id} onClick={() => toggleAlbum(a.id)}
                    className={`text-[10px] px-2 py-0.5 rounded border ${
                      on
                        ? 'bg-[#BA1826] text-white border-[#BA1826]'
                        : 'bg-white text-[#4B5563] border-[#E5E7EB]'
                    }`}
                    data-testid={`media-edit-album-${a.slug}`}>
                    {a.is_protected && <Lock className="w-2 h-2 inline mr-0.5" />}
                    {a.name}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Status */}
          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold
                         text-[#4B5563] mb-1">Status</p>
            <select value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              className="text-[11px] px-2 py-1.5 border border-[#E5E7EB] rounded bg-white"
              data-testid="media-edit-status">
              <option value="active">Active (visible on site)</option>
              <option value="pending">Pending review</option>
              <option value="archived">Archived (hidden)</option>
            </select>
          </div>

          {/* Drive status */}
          <div className="border border-[#E5E7EB] rounded p-2 text-[10px]"
            data-testid="media-drive-status">
            <p className="uppercase tracking-wider font-bold text-[#4B5563] mb-1">
              Google Drive mirror
            </p>
            {m.drive_file_id ? (
              <div className="text-emerald-800 inline-flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" />
                Synced ·{' '}
                <a href={m.drive_web_view_link} target="_blank"
                  rel="noopener noreferrer" className="underline">
                  Open in Drive
                </a>
              </div>
            ) : m.drive_sync_error ? (
              <div className="text-rose-700">Failed: {m.drive_sync_error}</div>
            ) : (
              <div className="text-[#9CA3AF]">
                Local only. Drive mirror activates once the Google sync is configured.
              </div>
            )}
          </div>

          {err && <p className="text-[11px] text-rose-700"
                 data-testid="media-edit-error">{err}</p>}

          {/* Actions */}
          <div className="flex items-center gap-2 pt-2 border-t border-[#F1F5F9]">
            <button onClick={save} disabled={saving}
              className="text-xs px-3 py-1.5 bg-[#BA1826] text-white rounded
                        inline-flex items-center gap-1 disabled:opacity-60"
              data-testid="media-edit-save">
              {saving && <Loader2 className="w-3 h-3 animate-spin" />}
              <Save className="w-3 h-3" /> Save
            </button>
            <button onClick={() => replaceInputRef.current?.click()}
              disabled={replacing}
              className="text-xs px-3 py-1.5 border border-[#E5E7EB] rounded
                        inline-flex items-center gap-1 disabled:opacity-60"
              data-testid="media-edit-replace">
              {replacing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
              Replace file
            </button>
            <input ref={replaceInputRef} type="file" accept="image/*"
              className="hidden"
              onChange={(e) => doReplace(e.target.files?.[0])} />
            <div className="flex-1" />
            <button onClick={doDelete}
              className="text-xs px-3 py-1.5 text-rose-700 hover:bg-rose-50
                        rounded inline-flex items-center gap-1"
              data-testid="media-edit-delete">
              <Trash2 className="w-3 h-3" /> Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, multiline, testid }) => (
  <div>
    <p className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563] mb-0.5">
      {label}
    </p>
    {multiline ? (
      <textarea value={value || ''} onChange={(e) => onChange(e.target.value)}
        rows={2}
        className="w-full text-xs px-2 py-1.5 border border-[#E5E7EB] rounded"
        data-testid={testid} />
    ) : (
      <input type="text" value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full text-xs px-2 py-1.5 border border-[#E5E7EB] rounded"
        data-testid={testid} />
    )}
  </div>
);

export default MediaLibraryPanel;
